import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  AccountCircle,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';

export const UserDropdown: React.FC = () => {
  const { instance, accounts } = useMsal();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const account = accounts[0];
  if (!account) return null;

  const handleClick = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogout = () => {
    // Clear local cache to sign out from this app only
    // This keeps the user signed into Microsoft for other apps
    instance.clearCache();
    // Redirect to login page
    window.location.href = '/';
    handleClose();
  };

  const getUserInitials = (name: string) =>
    name.split(' ').map(w => w.charAt(0)).join('').toUpperCase().slice(0, 2);

  const getAvatarColor = (name: string) => {
    const colors = [
      '#1976d2','#388e3c','#f57c00','#7b1fa2','#c2185b',
      '#00796b','#5d4037','#455a64','#e91e63','#ff5722'
    ];
    return colors[name.length % colors.length];
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <IconButton
        onClick={handleClick}
        size="small"
        sx={{
          ml: 1,
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: getAvatarColor(account.name || account.username),
            fontSize: '0.875rem',
            fontWeight: 600,
          }}
        >
          {getUserInitials(account.name || account.username)}
        </Avatar>
      </IconButton>

      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 3,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            minWidth: 200,
            '& .MuiAvatar-root': { width: 32, height: 32, ml: -0.5, mr: 1 },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem disabled>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: getAvatarColor(account.name || account.username),
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            {getUserInitials(account.name || account.username)}
          </Avatar>
          <Box sx={{ ml: 1 }}>
            <Typography variant="subtitle2" noWrap>
              {account.name || account.username}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {account.username}
            </Typography>
          </Box>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleClose}>
          <ListItemIcon><AccountCircle fontSize="small" /></ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleClose}>
          <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleLogout}>
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Sign Out</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};
